import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
  Link as LinkIcon,
  Loader2,
  Rocket,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { InstructionTrigger } from '@/features/instructions/InstructionDrawer';
import { useGenerateFormToken, useOnboardingForClient, useUpdateCompletion } from './useOnboarding';
import type { CompletionWithStep, OnboardingFormSchema } from './types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId: string;
  clientName: string;
}

export function OnboardingPanel({ open, onOpenChange, clientId, clientName }: Props) {
  const { data, isLoading } = useOnboardingForClient(open ? clientId : null);
  const update = useUpdateCompletion();

  const completions = data?.completions ?? [];
  const flow = data?.flow ?? null;

  const total = completions.length;
  const done = completions.filter((c) => c.status === 'completed' || c.status === 'skipped').length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-brand">
            <Rocket className="h-5 w-5 text-primary" />
            Onboarding — {clientName}
          </DialogTitle>
          <DialogDescription>
            Checklist dos 8 passos. Concluir todos ativa o cliente automaticamente.{' '}
            <InstructionTrigger
              contextKey="onboarding-link"
              variant="inline"
              label="Como gerar link público"
            />
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !flow ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Cliente ainda não está em onboarding. Mude o status para "Lead" ou "Onboarding" para
            iniciar.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-secondary/30 p-3">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium">
                  Progresso: {done}/{total} passos
                </span>
                <span className="text-muted-foreground">{pct}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-income transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Iniciado em{' '}
                {format(new Date(flow.started_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                {flow.status === 'completed' && flow.completed_at && (
                  <>
                    {' · '}Finalizado em{' '}
                    {format(new Date(flow.completed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </>
                )}
              </p>
            </div>

            <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {completions.map((c) => (
                <CompletionRow
                  key={c.id}
                  completion={c}
                  flowId={flow.id}
                  onMark={(status, notes) =>
                    update.mutate({
                      completionId: c.id,
                      status,
                      ...(notes !== undefined && { notes }),
                    })
                  }
                  busy={update.isPending}
                />
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RowProps {
  completion: CompletionWithStep;
  flowId: string;
  onMark: (status: 'pending' | 'completed' | 'skipped', notes?: string | null) => void;
  busy: boolean;
}

function CompletionRow({ completion, flowId, onMark, busy }: RowProps) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [notes, setNotes] = useState(completion.notes ?? '');
  const generate = useGenerateFormToken();

  const isDone = completion.status === 'completed';
  const isSkipped = completion.status === 'skipped';
  const step = completion.step;
  const schema = step.form_schema as OnboardingFormSchema | null;

  const handleGenerateLink = async () => {
    try {
      const result = await generate.mutateAsync({
        flowId,
        stepTemplateId: step.id,
      });
      try {
        await navigator.clipboard.writeText(result.url);
        toast.success('Link copiado para a área de transferência!');
      } catch {
        toast.success('Link gerado. Copie do modal.');
      }
      setShowLinkModal(true);
    } catch {
      // toast já emitido pela mutation
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 transition-shadow ${
        isDone
          ? 'border-income/30 bg-income/5'
          : isSkipped
            ? 'border-border bg-secondary/40'
            : 'border-border bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => onMark(isDone ? 'pending' : 'completed', notes || null)}
          disabled={busy}
          className="mt-0.5 shrink-0"
          title={isDone ? 'Marcar como pendente' : 'Marcar como concluído'}
        >
          {isDone ? (
            <CheckCircle2 className="h-5 w-5 text-income" />
          ) : isSkipped ? (
            <X className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`font-medium ${
                isDone || isSkipped ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {step.order_index}. {step.title}
            </span>
            {step.kind === 'client_form' && (
              <Badge
                variant="outline"
                className="border-primary/20 bg-primary/5 text-[10px] text-primary"
              >
                Cliente preenche
              </Badge>
            )}
          </div>
          {step.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
          )}
          {completion.data && Object.keys(completion.data).length > 0 && (
            <button
              type="button"
              onClick={() => setShowDataModal(true)}
              className="mt-1 text-xs text-primary hover:underline"
            >
              Ver dados preenchidos pelo cliente
            </button>
          )}
          {completion.notes && (
            <p className="mt-1 text-xs italic text-muted-foreground">"{completion.notes}"</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {step.kind === 'client_form' && !isDone && schema && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateLink}
              disabled={generate.isPending}
              className="h-7 text-xs"
            >
              {generate.isPending ? (
                <Loader2 size={12} className="mr-1 animate-spin" />
              ) : (
                <LinkIcon size={12} className="mr-1" />
              )}
              Gerar link
            </Button>
          )}
          {!isDone && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMark('skipped')}
              className="h-7 text-xs text-muted-foreground"
            >
              Pular
            </Button>
          )}
          {!isDone && step.kind === 'internal' && (
            <Textarea
              placeholder="Notas (opcional)"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 h-12 w-48 text-xs"
            />
          )}
        </div>
      </div>

      {/* Modal de link gerado */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link gerado</DialogTitle>
            <DialogDescription>
              Compartilhe com o cliente — válido por {generate.data?.expires_in_days ?? 14} dias.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                readOnly
                value={generate.data?.url ?? ''}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded border border-border bg-secondary/30 px-3 py-2 text-xs"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  if (generate.data?.url) {
                    void navigator.clipboard.writeText(generate.data.url);
                    toast.success('Copiado!');
                  }
                }}
              >
                <Copy size={14} />
              </Button>
              {generate.data?.url && (
                <Button size="icon" variant="outline" asChild>
                  <a href={generate.data.url} target="_blank" rel="noreferrer">
                    <ExternalLink size={14} />
                  </a>
                </Button>
              )}
            </div>
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <AlertCircle size={12} className="mr-1 inline" />
              Cuidado: qualquer pessoa com o link pode preencher o formulário. Compartilhe apenas
              com o responsável do cliente.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowLinkModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de dados preenchidos */}
      <Dialog open={showDataModal} onOpenChange={setShowDataModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Dados preenchidos — {step.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {schema?.fields.map((f) => {
              const value = (completion.data as Record<string, unknown>)?.[f.name];
              return (
                <div key={f.name} className="rounded border border-border p-2">
                  <p className="text-xs font-semibold text-muted-foreground">{f.label}</p>
                  <p className="mt-0.5 break-words text-sm text-foreground">
                    {value ? (
                      String(value)
                    ) : (
                      <span className="italic text-muted-foreground">—</span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowDataModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
